import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ExportDocumentsPresenter } from 'src/components/exportdocuments/export_documents_presenter'

import GroupManagerKeys from '../admin/group-manager/gm_keys'
import { WaitingScope } from '../the-connection/tc_scopes'
import UserProfileKeys from '../user-profile/up_keys'
import { crmFormFromConnection, crmFormToConnectionInput } from './crm_integration_form'
import { CrmIntegrationService, ehAdminDoGrupo, type OverflowPolicy } from './crm_integration_service'
import GroupProfileKeys from './gp_keys'
import {
    GroupProfileScope,
    HeaderScope,
    novaCrmApiKeyForm,
    novaCrmConnectionForm,
    type CrmApiKeyForm,
    type CrmConnectionForm,
    type HistoryQueryFilter
} from './gp_scopes'
import { GroupProfileService } from './gp_service'

const LOG = Logger.get('GroupProfilePresenter')
const groupProfileService = GroupProfileService.singleton()
const crmService = CrmIntegrationService.singleton()
const appStorage = AppStorage.singleton()

export class GroupProfilePresenter extends CubePresenter<MainPresenter, GroupProfileScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, GroupProfilePresenter>

    private readonly __exportDocsPresenter: ExportDocumentsPresenter

    #groupId?: number
    #origin?: string
    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new GroupProfileScope())
        this.__headerBar = new Presenter(this, this.scope.header)
        this.__exportDocsPresenter = new ExportDocumentsPresenter(this, this.scope.exportDocsScope)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        this.__exportDocsPresenter.release()
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new GroupProfileKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
            this.#initializeCrmIntegration()
        }

        if (
            (keys.startMonthFilter && this.scope.filterScope.filters.startMonth !== keys.startMonthFilter) ||
            this.scope.filterScope.filters.endMonth !== keys.endMonthFilter ||
            this.scope.filterScope.filters.page !== keys.pageFilter ||
            this.scope.filterScope.filters.limit !== keys.limitFilter
        ) {
            this.scope.filterScope.filters.startMonth =
                keys.startMonthFilter || this.scope.filterScope.filters.startMonth
            this.scope.filterScope.filters.endMonth = keys.endMonthFilter
            this.scope.filterScope.filters.page = keys.pageFilter || this.scope.filterScope.filters.page
            this.scope.filterScope.filters.limit = keys.limitFilter || this.scope.filterScope.filters.limit
        }

        this.#groupId = keys.groupId
        this.#origin = keys.origin

        if (!this.scope.profileScope.profile || !this.scope.profileScope.profile.id) {
            await this.handleFind(this.scope.filterScope.filters)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new GroupProfileKeys(this.app, intent)
        keys.groupId = this.#groupId
        keys.origin = this.#origin
        keys.startMonthFilter = this.scope.filterScope.filters.startMonth
        keys.endMonthFilter = this.scope.filterScope.filters.endMonth
        keys.pageFilter = this.scope.filterScope.filters.page
        keys.limitFilter = this.scope.filterScope.filters.limit
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.filterScope.onFind = this.action(this.handleFind)
        this.scope.onGoBack = this.action(this.handleGoBack)
        this.scope.historyScope.onViewDocuments = this.action(this.handleViewDocuments)
        this.scope.profileScope.onToggleBlockExport = this.action(this.handleToggleBlockExport)
        this.__exportDocsPresenter.initialize((exportId, query) =>
            groupProfileService.fetchExportDocuments(this.#groupId ?? 0, exportId, query)
        )
    }

    private async handleViewDocuments(exportId: number) {
        await this.__exportDocsPresenter.open(exportId)
    }

    private async handleToggleBlockExport(value: boolean) {
        if (!this.#groupId) {
            return
        }

        const previous = this.scope.profileScope.profile.block_export_when_exceeded

        try {
            this.scope.profileScope.savingBlockExport = true
            this.scope.profileScope.profile = { ...this.scope.profileScope.profile, block_export_when_exceeded: value }
            await groupProfileService.updateBlockExportWhenExceeded(this.#groupId, value)
            this.app.notify(
                'success',
                value
                    ? 'Bloqueio de exportação ao exceder a franquia ativado'
                    : 'Bloqueio de exportação ao exceder a franquia desativado'
            )
        } catch (caught) {
            this.scope.profileScope.profile = {
                ...this.scope.profileScope.profile,
                block_export_when_exceeded: previous
            }
            LOG.error('handleToggleBlockExport', caught)
            this.app.notify('error', 'Não foi possível atualizar a configuração de bloqueio de exportação')
        } finally {
            this.scope.profileScope.savingBlockExport = false
        }
    }

    #initializeCrmIntegration() {
        const conn = this.scope.crmConnectionsScope
        conn.onNew = this.action(this.handleCrmNewConnection)
        conn.onEdit = this.action(this.handleCrmEditConnection)
        conn.onCancelForm = this.action(this.handleCrmCancelForm)
        conn.onFormChange = this.action(this.handleCrmFormChange)
        conn.onSubmit = this.action(this.handleCrmSubmit)
        conn.onDelete = this.action(this.handleCrmDelete)
        conn.onConfirmDelete = this.action(this.handleCrmConfirmDelete)
        conn.onCancelDelete = this.action(this.handleCrmCancelDelete)

        const keys = this.scope.crmApiKeysScope
        keys.onNew = this.action(this.handleApiKeyNew)
        keys.onCancelForm = this.action(this.handleApiKeyCancelForm)
        keys.onFormChange = this.action(this.handleApiKeyFormChange)
        keys.onGenerate = this.action(this.handleApiKeyGenerate)
        keys.onCloseGeneratedKey = this.action(this.handleApiKeyCloseGeneratedKey)
        keys.onCopyKey = this.action(this.handleApiKeyCopyKey)
        keys.onEdit = this.action(this.handleApiKeyEdit)
        keys.onEditChange = this.action(this.handleApiKeyEditChange)
        keys.onSaveEdit = this.action(this.handleApiKeySaveEdit)
        keys.onCancelEdit = this.action(this.handleApiKeyCancelEdit)
        keys.onRevoke = this.action(this.handleApiKeyRevoke)
        keys.onConfirmRevoke = this.action(this.handleApiKeyConfirmRevoke)
        keys.onCancelRevoke = this.action(this.handleApiKeyCancelRevoke)

        const reqs = this.scope.crmExportRequestsScope
        reqs.onReload = this.action(this.handleExportRequestsReload)
        reqs.onAuthorize = this.action(this.handleExportRequestAuthorize)
        reqs.onConfirmAuthorize = this.action(this.handleExportRequestConfirmAuthorize)
        reqs.onCancelAuthorize = this.action(this.handleExportRequestCancelAuthorize)
        reqs.onReprocess = this.action(this.handleExportRequestReprocess)
        reqs.onConfirmReprocess = this.action(this.handleExportRequestConfirmReprocess)
        reqs.onCancelReprocess = this.action(this.handleExportRequestCancelReprocess)
    }

    private async handleFind(filters: Partial<HistoryQueryFilter>) {
        this.scope.filterScope.filters = { ...this.scope.filterScope.filters, ...filters }

        await Promise.all([this.handleGetProfile(), this.handleFindHistory(), this.handleFindChartData()])
    }

    private async handleGetProfile() {
        if ((this.scope.profileScope.profile && this.scope.profileScope.profile.id) || !this.#groupId) {
            return
        }
        try {
            this.scope.profileScope.waiting = new WaitingScope()
            this.scope.profileScope.profile = await groupProfileService.fetchProfile(this.#groupId)
            this.#setupCrmIntegration()
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.profileScope.waiting = undefined
        }
    }

    #setupCrmIntegration() {
        const profile = this.scope.profileScope.profile
        const isAdmin = ehAdminDoGrupo(appStorage.userName, profile)

        const podeGerenciar = isAdmin || appStorage.userManagerEnabled
        this.scope.canManageCrm = podeGerenciar && !!profile.crm_enrichment_enabled

        if (!this.scope.canManageCrm || !this.#groupId) {
            return
        }

        void this.action(this.handleCrmReloadConnections)()
        void this.action(this.handleApiKeysReload)()
        void this.action(this.handleExportRequestsReload)()
    }

    private async handleFindHistory() {
        try {
            if (!this.#groupId) {
                return
            }
            this.scope.historyScope.history_working = true
            this.scope.historyScope.history = await groupProfileService.fetchHistory(
                this.#groupId,
                this.scope.filterScope.filters
            )
            this.scope.historyScope.historyVersion++
        } finally {
            this.scope.historyScope.history_working = false
        }
    }

    private async handleFindChartData() {
        try {
            if (!this.#groupId) {
                return
            }
            this.scope.chartScope.history_chart_working = true
            this.scope.chartScope.historyChart = await groupProfileService.fetchChartHistory(
                this.#groupId,
                this.scope.filterScope.filters
            )
        } finally {
            this.scope.chartScope.history_chart_working = false
        }
    }

    private async handleCrmReloadConnections() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmConnectionsScope
        try {
            scope.loading = true
            const connections = await crmService.listConnections(groupId)
            scope.connections = connections
            this.scope.crmApiKeysScope.connections = connections
        } catch (caught) {
            this.notifyCrmError('Falha ao carregar conexões de CRM', caught)
        } finally {
            scope.loading = false
        }
    }

    private async handleCrmNewConnection() {
        const scope = this.scope.crmConnectionsScope
        scope.editingId = undefined
        scope.form = novaCrmConnectionForm()
        scope.formOpen = true
    }

    private async handleCrmEditConnection(connectionId: number) {
        const scope = this.scope.crmConnectionsScope
        const conn = scope.connections.find((c) => c.id === connectionId)
        if (!conn) {
            this.app.notify('warning', 'Conexão não encontrada')
            return
        }
        scope.editingId = connectionId
        scope.form = crmFormFromConnection(conn)
        scope.formOpen = true
    }

    private async handleCrmCancelForm() {
        this.scope.crmConnectionsScope.formOpen = false
    }

    private async handleCrmFormChange(patch: Partial<CrmConnectionForm>) {
        const scope = this.scope.crmConnectionsScope
        scope.form = { ...scope.form, ...patch }
    }

    private async handleCrmSubmit() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmConnectionsScope
        const form = scope.form

        if (!form.crm_type.trim()) {
            this.app.notify('warning', 'Informe o tipo de CRM da conexão')
            return
        }
        if (!form.name.trim()) {
            this.app.notify('warning', 'Informe um nome para a conexão')
            return
        }
        if (!form.endpoint_base.trim()) {
            this.app.notify('warning', 'Informe a URL base (endpoint) da conexão')
            return
        }

        try {
            scope.saving = true
            const input = crmFormToConnectionInput(form)
            if (scope.editingId) {
                await crmService.updateConnection(groupId, scope.editingId, input)
            } else {
                await crmService.createConnection(groupId, input)
            }
            scope.formOpen = false
            await this.handleCrmReloadConnections()
            this.app.notify('success', 'Conexão salva com sucesso')
        } catch (caught) {
            this.notifyCrmError('Falha ao salvar a conexão', caught)
        } finally {
            scope.saving = false
        }
    }

    private async handleCrmDelete(connectionId: number) {
        const scope = this.scope.crmConnectionsScope
        scope.deleteTarget = scope.connections.find((c) => c.id === connectionId)
    }

    private async handleCrmConfirmDelete() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmConnectionsScope
        const target = scope.deleteTarget
        if (!target) {
            return
        }
        try {
            await crmService.deleteConnection(groupId, target.id)
            scope.deleteTarget = undefined
            await this.handleCrmReloadConnections()
            this.app.notify('success', 'Conexão removida')
        } catch (caught) {
            this.notifyCrmError('Falha ao remover a conexão', caught)
        }
    }

    private async handleCrmCancelDelete() {
        this.scope.crmConnectionsScope.deleteTarget = undefined
    }

    private async handleApiKeysReload() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmApiKeysScope
        try {
            scope.loading = true
            scope.apiKeys = await crmService.listApiKeys(groupId)
        } catch (caught) {
            this.notifyCrmError('Falha ao carregar API-keys', caught)
        } finally {
            scope.loading = false
        }
    }

    private async handleApiKeyNew() {
        const scope = this.scope.crmApiKeysScope
        const form: CrmApiKeyForm = novaCrmApiKeyForm()
        form.connectionId = scope.connections[0]?.id
        scope.form = form
        scope.generatedKey = undefined
        scope.copied = false
        scope.formOpen = true
    }

    private async handleApiKeyCancelForm() {
        this.scope.crmApiKeysScope.formOpen = false
    }

    private async handleApiKeyFormChange(patch: Partial<CrmApiKeyForm>) {
        const scope = this.scope.crmApiKeysScope
        scope.form = { ...scope.form, ...patch }
    }

    private async handleApiKeyGenerate() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmApiKeysScope
        const form = scope.form

        if (!form.connectionId) {
            this.app.notify('warning', 'Selecione a conexão para a chave')
            return
        }
        if (!form.name.trim()) {
            this.app.notify('warning', 'Informe um nome para a chave')
            return
        }

        try {
            scope.saving = true
            const generated = await crmService.generateApiKey(groupId, form.connectionId, {
                name: form.name.trim(),
                expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T00:00:00Z`).toISOString() : undefined,
                overflowPolicy: form.overflowPolicy
            })
            scope.formOpen = false
            scope.copied = false
            scope.generatedKey = generated
            await this.handleApiKeysReload()
        } catch (caught) {
            this.notifyCrmError('Falha ao gerar a API-key', caught)
        } finally {
            scope.saving = false
        }
    }

    private async handleApiKeyCloseGeneratedKey() {
        const scope = this.scope.crmApiKeysScope
        scope.generatedKey = undefined
        scope.copied = false
    }

    private async handleApiKeyCopyKey() {
        const scope = this.scope.crmApiKeysScope
        const secret = scope.generatedKey?.apiKey
        if (!secret) {
            return
        }
        try {
            await navigator?.clipboard?.writeText(secret)
            scope.copied = true
            this.app.notify('success', 'Chave copiada para a área de transferência')
        } catch (caught) {
            LOG.warn('handleApiKeyCopyKey', caught)
            this.app.notify('warning', 'Não foi possível copiar automaticamente; copie manualmente')
        }
    }

    private async handleApiKeyEdit(apiKeyId: number) {
        const scope = this.scope.crmApiKeysScope
        const key = scope.apiKeys.find((k) => k.id === apiKeyId)
        if (!key) {
            this.app.notify('warning', 'Chave não encontrada')
            return
        }
        scope.editing = key
        scope.editName = key.name
        scope.editOverflowPolicy = key.overflow_policy
    }

    private async handleApiKeyEditChange(patch: { name?: string; overflowPolicy?: OverflowPolicy }) {
        const scope = this.scope.crmApiKeysScope
        if (patch.name !== undefined) {
            scope.editName = patch.name
        }
        if (patch.overflowPolicy !== undefined) {
            scope.editOverflowPolicy = patch.overflowPolicy
        }
    }

    private async handleApiKeySaveEdit() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmApiKeysScope
        const editing = scope.editing
        if (!editing) {
            return
        }
        try {
            await crmService.updateApiKey(groupId, editing.id, {
                name: scope.editName.trim() || undefined,
                overflowPolicy: scope.editOverflowPolicy
            })
            scope.editing = undefined
            await this.handleApiKeysReload()
            this.app.notify('success', 'Chave atualizada')
        } catch (caught) {
            this.notifyCrmError('Falha ao atualizar a chave', caught)
        }
    }

    private async handleApiKeyCancelEdit() {
        this.scope.crmApiKeysScope.editing = undefined
    }

    private async handleApiKeyRevoke(apiKeyId: number) {
        const scope = this.scope.crmApiKeysScope
        scope.revokeTarget = scope.apiKeys.find((k) => k.id === apiKeyId)
    }

    private async handleApiKeyConfirmRevoke() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmApiKeysScope
        const target = scope.revokeTarget
        if (!target) {
            return
        }
        try {
            await crmService.revokeApiKey(groupId, target.id)
            scope.revokeTarget = undefined
            await this.handleApiKeysReload()
            this.app.notify('success', 'Chave revogada')
        } catch (caught) {
            this.notifyCrmError('Falha ao revogar a chave', caught)
        }
    }

    private async handleApiKeyCancelRevoke() {
        this.scope.crmApiKeysScope.revokeTarget = undefined
    }

    private async handleExportRequestsReload() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmExportRequestsScope
        try {
            scope.loading = true
            const { records } = await crmService.listExportRequests(groupId, { limit: 100 })
            scope.requests = records
        } catch (caught) {
            this.notifyCrmError('Falha ao carregar solicitações de exportação', caught)
        } finally {
            scope.loading = false
        }
    }

    private async handleExportRequestAuthorize(requestId: number) {
        const scope = this.scope.crmExportRequestsScope
        scope.authorizeTarget = scope.requests.find((r) => r.id === requestId)
    }

    private async handleExportRequestConfirmAuthorize() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmExportRequestsScope
        const target = scope.authorizeTarget
        if (!target) {
            return
        }
        try {
            scope.authorizing = true
            await crmService.authorizeExportRequest(groupId, target.id)
            scope.authorizeTarget = undefined
            await this.handleExportRequestsReload()
            this.app.notify('success', 'Solicitação autorizada')
        } catch (caught) {
            this.notifyCrmError('Falha ao autorizar a solicitação', caught)
        } finally {
            scope.authorizing = false
        }
    }

    private async handleExportRequestCancelAuthorize() {
        this.scope.crmExportRequestsScope.authorizeTarget = undefined
    }

    private async handleExportRequestReprocess(requestId: number) {
        const scope = this.scope.crmExportRequestsScope
        scope.reprocessTarget = scope.requests.find((r) => r.id === requestId)
    }

    private async handleExportRequestConfirmReprocess() {
        const groupId = this.resolveGroupId()
        if (!groupId) {
            return
        }
        const scope = this.scope.crmExportRequestsScope
        const target = scope.reprocessTarget
        if (!target) {
            return
        }
        try {
            scope.reprocessing = true
            await crmService.reprocessExportRequest(groupId, target.id)
            scope.reprocessTarget = undefined
            await this.handleExportRequestsReload()
            this.app.notify('success', 'Solicitação reenfileirada para reprocessamento')
        } catch (caught) {
            this.notifyCrmError('Falha ao reprocessar a solicitação', caught)
        } finally {
            scope.reprocessing = false
        }
    }

    private async handleExportRequestCancelReprocess() {
        this.scope.crmExportRequestsScope.reprocessTarget = undefined
    }

    private resolveGroupId(): number | undefined {
        return this.scope.profileScope?.profile?.id
    }

    private notifyCrmError(prefixo: string, caught: unknown) {
        LOG.error(prefixo, caught)
        const detalhe =
            caught && typeof caught === 'object' && 'message' in caught
                ? String((caught as { message?: unknown }).message)
                : String(caught)
        this.app.notify('error', `${prefixo}: ${detalhe}`)
    }

    private async handleGoBack() {
        if (this.#origin && this.#origin === 'user-profile') {
            const key = new UserProfileKeys(this.app)
            await key.flip()
            return
        }
        const key = new GroupManagerKeys(this.app)
        await key.flip()
    }
}

export default GroupProfilePresenter
